// bm-design-system: button primitive
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva("btn", {
  variants: {
    variant: {
      primary: "btn-primary",
      secondary: "btn-secondary",
      ghost: "btn-ghost",
      soft: "btn-soft",
      danger: "btn-danger",
      link: "btn-link",
      // Public site (Private Cellar): square, uppercase, letter-spaced.
      gold: "btn-gold",
      "ghost-hairline": "btn-ghost-hairline",
    },
    size: {
      sm: "btn-small",
      md: "",
      lg: "btn-large",
      icon: "btn-icon",
      // 44px square: the touch-target icon button (admin, M7).
      "icon-lg": "btn-icon-large",
    },
  },
  // The public variants reuse the sm / md / lg size names but map them to the
  // mockup's .btn-sm / .btn-lg metrics, leaving admin sizes untouched.
  compoundVariants: [
    { variant: ["gold", "ghost-hairline"], size: ["sm", "md"], className: "btn-public-sm" },
    { variant: ["gold", "ghost-hairline"], size: "lg", className: "btn-public-lg" },
  ],
  defaultVariants: {
    variant: "primary",
    size: "md",
  },
});

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
